/**
 * Cross-cutting UI state: the toast, the app-level modals, and the navigation
 * sidebar's open/closed state.
 *
 * Not one of the product domains, but genuinely global: `showToast` is called
 * from nearly every feature, and the out-of-credits modal is raised by the
 * credits check inside Create, Genre Mixer and Studio alike. Threading a
 * callback down to all of them was a large part of what made the monolith's prop
 * flow unmanageable.
 *
 * THE SIDEBAR DRAWER belongs here for the same reason: it is one sidebar, and the
 * hamburger that opens it, the drawer overlay, the route change that dismisses it and
 * the Escape key that closes it are four different places acting on one piece of state.
 * It is deliberately NOT a second navigation — the sidebar is still the one in
 * components/layout/Sidebar.tsx with the same rows; this only says whether it is
 * showing.
 *
 * NOTHING HERE IS PERSISTED. There used to be one exception, `navCollapsed`, remembering
 * whether the sidebar had been narrowed to its icon rail; the button that set it has
 * been removed, so the state and its `Lyrixa.navCollapsed` storage key went with it. The
 * rail still exists below 900px, decided by a media query rather than by state.
 *
 * Feature-local form fields, accordions and tab selections stay in their components.
 * The Settings & Resources disclosure is the narrow exception: it lives here so a
 * route-shell remount cannot close it during navigation, but remains session-only.
 */
import { create } from 'zustand';
import type { LyricsSurface, Playlist, PublisherRelease, Song } from '../types/domain';

export type DeleteSongSurface = 'library' | 'generatedResult';
export type PublisherDeleteSurface = 'dashboard' | 'detail';

/** How long a toast stays visible, in ms. Long enough to read comfortably. */
const TOAST_DURATION_MS = 10_000;

/** Keep private provider and model names out of every user-facing toast. */
export function sanitizeToastMessage(message: string): string {
  return message
    .replace(
      /(?:GOOGLE_AI_STUDIO_API_KEY|REPLICATE_API_TOKEN|TTAPI_KEY|USEAPI_TOKEN|LALAL_LICENSE_KEY)/g,
      'AI service configuration'
    )
    .replace(/Google\s+Lyria(?:\s*3(?:\.5)?(?:\s+Pro)?)?/gi, 'AI generation service')
    .replace(/lyria-3(?:\.5|-(?:clip|pro)(?:-preview)?)?/gi, 'AI generation service')
    .replace(/Lyria(?:\s*3(?:\.5)?(?:\s+Pro)?)?/gi, 'AI generation service')
    .replace(/Google(?:'s|’s)/gi, "Our AI service's")
    .replace(/Google(?:\s+AI\s+Studio)?|Gemini(?:\s+[\w.-]+)?/gi, 'AI service')
    .replace(
      /Replicate|Stable\s+Audio|MusicGen|MiniMax|Flow\s*Music|TT-?API|UseAPI/gi,
      'AI service'
    )
    .replace(/LALAL(?:\.AI)?|Demucs/gi, 'audio service')
    .replace(/Resend/gi, 'email service')
    .replace(/LabelGrid/gi, 'publishing service');
}

interface UiState {
  toast: string | null;
  /** Song pending delete confirmation, or null. */
  deleteTarget: Song | null;

  /**
   * Which surface raised `deleteTarget`, and therefore which copy the confirmation shows.
   *
   * The generated-result card wants a shorter sentence than the Library and Studio do, and
   * that is the same "one target, one flavour" shape `lyricsTarget` already uses. A second
   * delete target would be the wrong split for the same reason: the question "is there a song
   * pending deletion" has one answer, and the wording is a second field on that one answer.
   */
  deleteSurface: DeleteSongSurface;

  /** Publisher release pending dashboard-delete confirmation, or null. */
  publisherDeleteTarget: PublisherRelease | null;
  /** Which Publisher surface raised the delete dialog. */
  publisherDeleteSurface: PublisherDeleteSurface;

  /** Song being edited, or null. */
  editTarget: Song | null;
  /** True when the out-of-credits modal is open. */
  outOfCreditsOpen: boolean;
  /** True when the delete-account modal is open. */
  deleteAccountOpen: boolean;
  /** True when the change-password modal is open. */
  changePasswordOpen: boolean;

  /**
   * True when the profile-picture chooser is open.
   *
   * A boolean, not a target: unlike the song and playlist dialogs below, there is only
   * ever one account to edit, so there is nothing to carry — the dialog reads the
   * current picture from `authStore` the same way every other avatar surface does.
   * Sitting alongside `deleteAccountOpen`, which is the other account dialog and takes
   * the same shape for the same reason.
   */
  profilePictureOpen: boolean;

  /**
   * Song whose lyrics preview is open, or null.
   *
   * THE HOME PAGE'S ANSWER TO "what happens when you click a song". A visitor browsing
   * the catalogue is discovering music, not editing it, so a card opens this dialog
   * rather than navigating into the Studio — the Studio is the creation environment and
   * the two must not blur. Holding the target here rather than in the route's local state
   * is what keeps that true from anywhere: any surface showing songs can raise the same
   * preview without growing its own copy of the dialog.
   *
   * A Song rather than an id, like the four targets below it, for the same reason: the
   * dialog needs the title, the artwork and the sheet, and a target that carries the
   * record cannot be opened against a song that has since been deleted.
   */
  lyricsTarget: Song | null;

  /**
   * Which surface raised `lyricsTarget`, and therefore which actions the dialog offers.
   *
   * A SECOND FIELD RATHER THAN A SECOND TARGET, which is the choice worth explaining. The
   * alternative was `libraryLyricsTarget` alongside the one above — and that is how you end
   * up with two dialogs open at once the first time a surface sets one without clearing the
   * other, plus every consumer reading two fields to answer "is the preview showing". One
   * target answers that; this says which flavour of it.
   *
   * NOT part of `lyricsTarget` itself either: the target is a `Song`, the same record every
   * other dialog target holds, and wrapping it in `{song, surface}` would make every
   * existing read narrow through a wrapper to get at a title.
   *
   * Meaningless while `lyricsTarget` is null, and deliberately not reset on close — the
   * next `setLyricsTarget` sets it, and a stale value on a closed dialog is unobservable.
   * See types/domain.LyricsSurface for what each value means and Batch 6 §9 for why the
   * distinction exists at all.
   */
  lyricsSurface: LyricsSurface;

  /**
   * Song whose "Add to playlist" sheet is open, or null.
   *
   * A Song rather than a boolean plus an id held elsewhere, matching `editTarget` and
   * `deleteTarget`: the dialog needs the title for its heading, and a target that
   * carries the record cannot be opened against a song that has since been deleted.
   *
   * Three separate targets rather than one `playlistDialog: {kind, …}` discriminated
   * union, because each of the three is a different type — a Song for the add sheet, a
   * Playlist for rename and delete — and a union would make every consumer narrow
   * before it could read a name.
   */
  addToPlaylistTarget: Song | null;

  /**
   * Song pending the "Make this song discoverable?" confirmation, or null.
   *
   * A Song rather than an id, for the reason every target above gives: the dialog names
   * the track, and a target carrying the record cannot be confirmed against a song that
   * has since been deleted.
   *
   * TWO TARGETS RATHER THAN ONE `visibilityTarget`, even though both dialogs end in a
   * `setVisibility` call. They ask opposite questions, so a single target would have to
   * carry a direction alongside the song and every consumer would read the song's current
   * visibility to work out which dialog it was looking at. Two names, two dialogs, no
   * narrowing — the same argument the three playlist targets make above.
   */
  makeDiscoverableTarget: Song | null;
  /** Song pending the "Remove from Lyrixa Catalogue?" confirmation, or null. */
  removeFromCatalogueTarget: Song | null;

  /** Playlist pending rename, or null. */
  renamePlaylistTarget: Playlist | null;
  /** Playlist pending delete confirmation, or null. */
  deletePlaylistTarget: Playlist | null;

  /**
   * True when the sidebar is open as an overlay drawer.
   *
   * Phone-only in effect, and deliberately NOT remembered: a drawer covering the
   * screen is a thing you opened a moment ago, and restoring it on load would greet
   * every visit with a full-screen menu.
   */
  navDrawerOpen: boolean;
  /** Session-only disclosure state for the Settings & Resources sidebar group. */
  navResourcesGroupOpen: boolean;

  /**
   * Whether the mini player is open for the current playback session.
   *
   * THIS IS NOT "IS SOMETHING PLAYING". That is the whole reason it exists, and the
   * distinction is the correction this field carries: playing a song is not a request for a
   * floating bar. Starting a track from a Library row leaves this false, so the bar does not
   * arrive over the screen the user is already looking at, where the row itself shows the
   * position, the waveform and a pause control.
   *
   * TWO THINGS OPEN IT, and only two:
   *
   *   1. THE USER ASKS. "Play in Mini Player" on a song row (see usePlayer's
   *      `playInMiniPlayer`) and the reveal control on the Create screens (see AppShell).
   *   2. THE USER LEAVES THE SCREEN PLAYBACK IS RUNNING ON. A navigation while a track is
   *      advancing is what the bar is for — it is the only surface that carries playback
   *      out of the page it was started from. AppShell owns that effect, because the shell
   *      is the one thing that knows the route and survives the change of it.
   *
   * ONE THING CLOSES IT: the bar's own control, which stops playback in the same gesture —
   * see usePlayer's `stopAndCloseMiniPlayer`. Plus playback ending on its own, which
   * AppShell resets so the next session starts closed rather than inheriting this one's
   * answer.
   *
   * IT REPLACED A DISMISSAL KEYED ON THE SONG ID (`miniPlayerHiddenFor`), and the shape had
   * to change with the meaning. That field existed because closing the bar did NOT stop the
   * music: the track went on playing invisibly, so "closed" had to expire by itself or the
   * one surface showing playback would have been switched off for the rest of the session by
   * a single tap. Closing now stops the player, so there is no invisible playback to come
   * back for and nothing to key the flag to — a session that has been closed no longer
   * exists. A boolean is the honest shape of "is this open", and the defect the id was
   * guarding against (close it, pause, and it springs back) is not reachable at all now:
   * after a close there is nothing loaded to pause.
   *
   * A boolean here rather than in AppShell's own `useState` because two places write it —
   * the shell's navigation effect and reveal control, and the song rows' "Play in Mini
   * Player" by way of usePlayer — which is exactly the line this store's header draws.
   */
  miniPlayerOpen: boolean;

  /** Shows a toast, replacing any current one and restarting the timer. */
  showToast: (message: string) => void;
  hideToast: () => void;
  setDeleteTarget: (song: Song | null, surface?: DeleteSongSurface) => void;
  setPublisherDeleteTarget: (
    release: PublisherRelease | null,
    surface?: PublisherDeleteSurface
  ) => void;
  setEditTarget: (song: Song | null) => void;
  setOutOfCreditsOpen: (open: boolean) => void;
  setDeleteAccountOpen: (open: boolean) => void;
  setChangePasswordOpen: (open: boolean) => void;
  setProfilePictureOpen: (open: boolean) => void;
  /**
   * Opens (or closes) the song detail preview.
   *
   * `surface` defaults to `'catalogue'`, which is what makes this a widening rather than a
   * breaking change: the Home page, the featured hero, the trending rows and the playlist
   * screens all call `setLyricsTarget(song)` with one argument and keep the four discovery
   * actions they have always had. Only the Library passes `'library'`.
   */
  setLyricsTarget: (song: Song | null, surface?: LyricsSurface) => void;
  setAddToPlaylistTarget: (song: Song | null) => void;
  setMakeDiscoverableTarget: (song: Song | null) => void;
  setRemoveFromCatalogueTarget: (song: Song | null) => void;
  setRenamePlaylistTarget: (playlist: Playlist | null) => void;
  setDeletePlaylistTarget: (playlist: Playlist | null) => void;

  setNavDrawerOpen: (open: boolean) => void;
  toggleNavDrawer: () => void;
  setNavResourcesGroupOpen: (open: boolean) => void;
  toggleNavResourcesGroup: () => void;

  /**
   * Opens the mini player.
   *
   * Called when the user asks for it — "Play in Mini Player", or the reveal control on the
   * Create screens — and when they navigate away from the screen playback is running on.
   * NOT called when a song starts playing; that is the whole point of the field.
   */
  openMiniPlayer: () => void;

  /**
   * Closes the mini player. Does NOT stop playback on its own.
   *
   * A UI store cannot reach into the player, and should not: the pairing lives in
   * usePlayer's `stopAndCloseMiniPlayer`, which is what the bar's close button calls and
   * the only thing a user can press to get here. This is also how AppShell resets the flag
   * when playback ends by itself, where there is nothing left to stop.
   */
  closeMiniPlayer: () => void;
}

/**
 * Timer handle for the auto-dismiss.
 *
 * Module-scoped rather than stored in state: it is not rendered, and keeping a
 * mutable timer id out of the store means showing a toast triggers exactly one
 * re-render rather than two.
 */
let toastTimer: ReturnType<typeof setTimeout> | undefined;

export const useUiStore = create<UiState>((set, get) => ({
  toast: null,
  deleteTarget: null,
  deleteSurface: 'library',
  publisherDeleteTarget: null,
  publisherDeleteSurface: 'dashboard',
  editTarget: null,
  outOfCreditsOpen: false,
  deleteAccountOpen: false,
  changePasswordOpen: false,
  profilePictureOpen: false,
  lyricsTarget: null,
  lyricsSurface: 'catalogue',
  addToPlaylistTarget: null,
  makeDiscoverableTarget: null,
  removeFromCatalogueTarget: null,
  renamePlaylistTarget: null,
  deletePlaylistTarget: null,
  navDrawerOpen: false,
  navResourcesGroupOpen: false,
  miniPlayerOpen: false,

  showToast(message) {
    clearTimeout(toastTimer);
    set({ toast: sanitizeToastMessage(message) });
    toastTimer = setTimeout(() => set({ toast: null }), TOAST_DURATION_MS);
  },

  hideToast() {
    clearTimeout(toastTimer);
    set({ toast: null });
  },

  setDeleteTarget(deleteTarget, deleteSurface = 'library') {
    set({ deleteTarget, deleteSurface });
  },

  setPublisherDeleteTarget(publisherDeleteTarget, publisherDeleteSurface = 'dashboard') {
    set({ publisherDeleteTarget, publisherDeleteSurface });
  },

  setEditTarget(editTarget) {
    set({ editTarget });
  },

  setOutOfCreditsOpen(outOfCreditsOpen) {
    set({ outOfCreditsOpen });
  },

  setDeleteAccountOpen(deleteAccountOpen) {
    set({ deleteAccountOpen });
  },

  setChangePasswordOpen(changePasswordOpen) {
    set({ changePasswordOpen });
  },

  setProfilePictureOpen(profilePictureOpen) {
    set({ profilePictureOpen });
  },

  setLyricsTarget(lyricsTarget, lyricsSurface = 'catalogue') {
    // Both in one `set`, so there is no render between them where the dialog is open on a
    // song with the previous surface's actions.
    set({ lyricsTarget, lyricsSurface });
  },

  setAddToPlaylistTarget(addToPlaylistTarget) {
    set({ addToPlaylistTarget });
  },

  setMakeDiscoverableTarget(makeDiscoverableTarget) {
    set({ makeDiscoverableTarget });
  },

  setRemoveFromCatalogueTarget(removeFromCatalogueTarget) {
    set({ removeFromCatalogueTarget });
  },

  setRenamePlaylistTarget(renamePlaylistTarget) {
    set({ renamePlaylistTarget });
  },

  setDeletePlaylistTarget(deletePlaylistTarget) {
    set({ deletePlaylistTarget });
  },

  setNavDrawerOpen(navDrawerOpen) {
    set({ navDrawerOpen });
  },

  toggleNavDrawer() {
    set({ navDrawerOpen: !get().navDrawerOpen });
  },

  setNavResourcesGroupOpen(navResourcesGroupOpen) {
    set({ navResourcesGroupOpen });
  },

  toggleNavResourcesGroup() {
    set({ navResourcesGroupOpen: !get().navResourcesGroupOpen });
  },

  openMiniPlayer() {
    // Guarded rather than an unconditional set, in both directions: AppShell's navigation
    // effect calls one of these on every route change, and writing the value it already
    // holds would notify every subscriber of the UI store on each one.
    if (!get().miniPlayerOpen) set({ miniPlayerOpen: true });
  },

  closeMiniPlayer() {
    if (get().miniPlayerOpen) set({ miniPlayerOpen: false });
  },
}));

/*
  Removed in the Phase 2 cleanup: a module-level
  `showToast = (m) => useUiStore.getState().showToast(m)` for calling out of a
  component. Nothing used it — every one of the ~60 call sites is inside a
  component or a hook and takes the selector. If non-component code ever needs a
  toast, `useUiStore.getState().showToast(...)` is the one-liner, so the wrapper
  was speculative surface rather than a helper.
*/
